export type PlaygroundRole =
  | { kind: 'superAdmin' }
  | { kind: 'partnerAdmin' }
  | { kind: 'partner'; partnerId: string; isPartnerManager: boolean }

/** The role's name as the account menu shows it. */
export const playgroundRoleLabel = (role: PlaygroundRole): string => {
  if (role.kind === 'superAdmin') return 'Super admin'
  if (role.kind === 'partnerAdmin') return 'Partner admin'
  return role.isPartnerManager ? 'Partner manager' : 'Partner viewer'
}

/**
 * The role a set of ID token claims grants, or `null` for none.
 *
 * Null is the answer for anything unrecognised, which is what makes the guard
 * default-deny: a mobile app user, or somebody who created a password account on
 * the project, has no role and therefore no playground.
 */
export const resolvePlaygroundRole = (
  claims: Record<string, unknown>
): PlaygroundRole | null => {
  // Admin first: it is strictly the larger role, so an account holding both
  // should not be narrowed to one partner's view.
  if (claims.admin === true) return { kind: 'superAdmin' }

  // Above partner: it manages every partner, though it reads none of their users.
  if (claims.partnerAdmin === true) return { kind: 'partnerAdmin' }

  // ZL-1363. Mirrors `claimedPartnerId()` in the rules: a half-written grant
  // reaches nothing rather than everything, the same way in both layers.
  const partnerId = claims.partnerId
  if (typeof partnerId === 'string' && partnerId.length > 0) {
    return {
      kind: 'partner',
      partnerId,
      isPartnerManager: claims.partnerManager === true,
    }
  }

  return null
}
